import {
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Stethoscope,
} from 'lucide-react';
import * as React from 'react';

import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import type { DiscriminatedProviderCreatedEvent } from '@/services/api/generated/taskActivityService';

import {
  EventActions,
  EventBody,
  EventContainer,
  EventHeader,
} from './EventPrimitives';

interface ProviderCreatedEventProps {
  event: DiscriminatedProviderCreatedEvent;
  matterId: string;
}

export const ProviderCreatedEvent: React.FC<ProviderCreatedEventProps> = ({
  event,
  matterId,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const { payload, occurred_at, user, agent } = event;
  const { provider, needs_approval } = payload;

  // Get provider name
  const providerName = provider.name || 'Unknown Provider';

  // Get provider type
  const providerType = provider.provider_type
    ? formatProviderType(provider.provider_type)
    : null;

  // Get treatment status
  const treatmentStatus = provider.treatment_status
    ? formatTreatmentStatus(provider.treatment_status)
    : null;
  const treatmentStatusVariant = provider.treatment_status
    ? getTreatmentStatusVariant(provider.treatment_status)
    : undefined;

  // Get status badge
  const statusVariant = provider.status
    ? getStatusVariant(provider.status)
    : undefined;
  const statusLabel = provider.status ? formatStatus(provider.status) : null;

  // Count additional details
  const hasEncounters = provider.encounters && provider.encounters.length > 0;
  const hasHandles = provider.handles && provider.handles.length > 0;
  const hasTreatmentNotes = !!provider.treatment_notes;
  const hasDescription = !!provider.description;
  const hasAdditionalDetails =
    hasEncounters || hasHandles || hasTreatmentNotes || hasDescription;

  return (
    <EventContainer>
      <EventHeader
        icon={<Stethoscope size={20} />}
        title="Provider Created"
        timestamp={occurred_at}
        actor={user}
        agent={agent}
      />

      <EventBody>
        <div className="space-y-1.5">
          {/* Provider Name and Type */}
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <span className="font-medium">{providerName}</span>
            {providerType && (
              <>
                <span className="text-slate-400">•</span>
                <Badge variant="outline">{providerType}</Badge>
              </>
            )}
            {statusLabel && (
              <>
                <span className="text-slate-400">•</span>
                <Badge variant={statusVariant}>{statusLabel}</Badge>
              </>
            )}
          </div>

          {/* Treatment Status */}
          <div className="flex items-center gap-2 text-xs">
            <span className="text-slate-600">Treatment Status:</span>
            <Badge variant={treatmentStatusVariant}>{treatmentStatus}</Badge>
          </div>

          {/* Dates of Service */}
          {provider.dates_of_service && (
            <div className="text-xs">
              <span className="text-slate-600">Dates of Service:</span>{' '}
              <span>{provider.dates_of_service}</span>
            </div>
          )}

          {/* Treatment */}
          {provider.treatment && (
            <div className="text-xs">
              <span className="text-slate-600">Treatment:</span>{' '}
              <span>{provider.treatment}</span>
            </div>
          )}

          {/* Address */}
          {provider.address && (
            <div className="text-xs">
              <span className="text-slate-600">Address:</span>{' '}
              <span className="text-[11px]">{provider.address}</span>
            </div>
          )}

          {/* Contact Info */}
          {(provider.phone || provider.fax || provider.email) && (
            <div className="text-xs space-y-0.5">
              {provider.phone && (
                <div>
                  <span className="text-slate-600">Phone:</span>{' '}
                  <span className="font-mono text-[11px]">
                    {provider.phone}
                  </span>
                </div>
              )}
              {provider.fax && (
                <div>
                  <span className="text-slate-600">Fax:</span>{' '}
                  <span className="font-mono text-[11px]">{provider.fax}</span>
                </div>
              )}
              {provider.email && (
                <div>
                  <span className="text-slate-600">Email:</span>{' '}
                  <span className="text-[11px]">{provider.email}</span>
                </div>
              )}
            </div>
          )}

          {/* Collapsible Additional Details */}
          {hasAdditionalDetails && (
            <Collapsible open={isOpen} onOpenChange={setIsOpen}>
              <CollapsibleTrigger className="flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-800 mt-1.5">
                {isOpen ? (
                  <ChevronDown size={12} />
                ) : (
                  <ChevronRight size={12} />
                )}
                <span>{isOpen ? 'Hide' : 'Show'} additional details</span>
              </CollapsibleTrigger>

              <CollapsibleContent className="mt-2 space-y-2.5 border-l-2 border-slate-200 pl-2.5">
                {/* Description */}
                {hasDescription && (
                  <div>
                    <div className="text-[11px] font-medium text-slate-700 mb-0.5">
                      Description
                    </div>
                    <div className="text-[11px] text-slate-600">
                      {provider.description}
                    </div>
                  </div>
                )}

                {/* Treatment Notes */}
                {hasTreatmentNotes && (
                  <div>
                    <div className="text-[11px] font-medium text-slate-700 mb-0.5">
                      Treatment Notes
                    </div>
                    <div className="text-[11px] text-slate-600">
                      {provider.treatment_notes}
                    </div>
                  </div>
                )}

                {/* Encounters */}
                {hasEncounters && (
                  <div>
                    <div className="text-[11px] font-medium text-slate-700 mb-0.5">
                      Encounters ({provider.encounters?.length})
                    </div>
                    <div className="space-y-1.5">
                      {provider.encounters?.map((encounter, index) => (
                        <div
                          key={encounter.id}
                          className="text-[11px] space-y-0.5"
                        >
                          {encounter.date && (
                            <div className="font-medium">
                              {formatDate(encounter.date)}
                            </div>
                          )}
                          <div className="text-slate-600">
                            {encounter.description}
                          </div>
                          {encounter.services &&
                            encounter.services.length > 0 && (
                              <div className="text-slate-500">
                                Services: {encounter.services.join(', ')}
                              </div>
                            )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Contact Handles */}
                {hasHandles && (
                  <div>
                    <div className="text-[11px] font-medium text-slate-700 mb-0.5">
                      Contact Methods
                    </div>
                    <div className="space-y-1">
                      {provider.handles.map((handle, index) => (
                        <div key={index} className="text-[11px]">
                          <span className="text-slate-600">
                            {handle.handle_type}:
                          </span>{' '}
                          <span className="font-mono">{handle.value}</span>
                          {handle.name && (
                            <span className="text-slate-500 ml-1">
                              ({handle.name})
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>
      </EventBody>

      <EventActions>
        <a
          href={`/matters/${matterId}/treatment/${provider.id}`}
          className="text-[11px] text-blue-600 hover:text-blue-800 flex items-center gap-1 hover:underline"
        >
          View Provider
          <ExternalLink size={11} />
        </a>
      </EventActions>
    </EventContainer>
  );
};

// Helper functions for formatting
function formatProviderType(type: string): string {
  const types: Record<string, string> = {
    ORGANIZATION: 'Organization',
    PRACTITIONER: 'Practitioner',
  };
  return types[type] || type;
}

function formatTreatmentStatus(status: string): string {
  const statuses: Record<string, string> = {
    TREATING: 'Treating',
    COMPLETED: 'Completed',
    DEVIATING: 'Deviating',
    UNKNOWN: 'Unknown',
  };
  return statuses[status] || status;
}

function getTreatmentStatusVariant(
  status: string,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'TREATING':
      return 'secondary'; // Blue for active treatment
    case 'COMPLETED':
      return 'default'; // Green for completed
    case 'DEVIATING':
      return 'destructive'; // Red for deviating
    case 'UNKNOWN':
      return 'outline'; // Gray for unknown
    default:
      return 'outline';
  }
}

function formatStatus(status: string): string {
  const statuses: Record<string, string> = {
    NEEDS_APPROVAL: 'Needs Approval',
    APPROVED: 'Approved',
    REJECTED: 'Rejected',
  };
  return statuses[status] || status;
}

function getStatusVariant(
  status: string,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'APPROVED':
      return 'default'; // Green
    case 'REJECTED':
      return 'destructive'; // Red
    case 'NEEDS_APPROVAL':
      return 'secondary'; // Blue
    default:
      return 'outline';
  }
}

function formatDate(dateString: string): string {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return dateString;
  }
}
